import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/cheap-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Types ──────────────────────────────────────────────────────────────── */
type BudgetAge = '25' | '30' | '35' | '40' | '45' | '50' | '55';
type BudgetCoverage = '100k' | '250k' | '500k' | '1m';
type BudgetHealth = 'excellent' | 'good' | 'fair';

interface BudgetResult {
  carrier: string;
  amBest: string;
  type: string;
  monthlyRate: number;
  annualRate: number;
  annualSavings: number;
  approvalTime: string;
  notes: string;
  badge?: string;
  tip?: string;
}

/* ─── Rate Engine ────────────────────────────────────────────────────────── */
const CHEAP_BASE: Record<BudgetAge, Record<BudgetCoverage, number>> = {
  '25': { '100k': 10, '250k': 16, '500k': 23, '1m': 39 },
  '30': { '100k': 12, '250k': 19, '500k': 28, '1m': 48 },
  '35': { '100k': 15, '250k': 24, '500k': 36, '1m': 64 },
  '40': { '100k': 22, '250k': 36, '500k': 55, '1m': 97 },
  '45': { '100k': 34, '250k': 57, '500k': 88, '1m': 153 },
  '50': { '100k': 54, '250k': 90, '500k': 139, '1m': 245 },
  '55': { '100k': 88, '250k': 147, '500k': 227, '1m': 397 },
};

const HEALTH_MULT: Record<BudgetHealth, number> = {
  excellent: 0.88,
  good: 1.0,
  fair: 1.32,
};

const COVERAGE_AMOUNTS: Record<BudgetCoverage, number> = {
  '100k': 100000, '250k': 250000, '500k': 500000, '1m': 1000000,
};

function getBudgetResults(age: BudgetAge, coverage: BudgetCoverage, health: BudgetHealth): BudgetResult[] {
  const base = CHEAP_BASE[age][coverage];
  const hm = HEALTH_MULT[health];
  const m = Math.round(base * hm);
  const annual = m * 12;

  const results: BudgetResult[] = [
    {
      carrier: 'Banner Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: m,
      annualRate: annual,
      annualSavings: Math.round(annual * 0.18),
      approvalTime: '24–48 hrs',
      notes: 'Consistently the lowest term life rates in Indianapolis. Accelerated underwriting — no medical exam up to $1M.',
      badge: 'Cheapest Rate',
      tip: 'Apply online for fastest approval',
    },
    {
      carrier: 'Protective Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.04),
      annualRate: Math.round(m * 1.04) * 12,
      annualSavings: Math.round(annual * 0.14),
      approvalTime: '24–72 hrs',
      notes: 'Second-lowest rates in Indianapolis. Strong for minor health conditions. Excellent conversion rights.',
    },
    {
      carrier: 'Pacific Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.07),
      annualRate: Math.round(m * 1.07) * 12,
      annualSavings: Math.round(annual * 0.10),
      approvalTime: '1–2 weeks',
      notes: 'Top pick for $500K+ coverage. Competitive rates for Indianapolis professionals.',
    },
    {
      carrier: 'Prudential',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.10),
      annualRate: Math.round(m * 1.10) * 12,
      annualSavings: Math.round(annual * 0.06),
      approvalTime: '1–3 weeks',
      notes: 'Best for complex health histories. Lenient underwriting for Indianapolis applicants with controlled conditions.',
    },
    {
      carrier: 'AIG / Corebridge',
      amBest: 'A',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.14),
      annualRate: Math.round(m * 1.14) * 12,
      annualSavings: 0,
      approvalTime: '1–3 weeks',
      notes: 'Competitive for older applicants (45+). Good option for Indianapolis residents with some health history.',
    },
  ];

  return results;
}

/* ─── Money-Saving Tips ──────────────────────────────────────────────────── */
const SAVING_TIPS = [
  {
    tip: 'Buy Term, Not Whole Life',
    savings: 'Save 80–90%',
    desc: 'A $500K 20-year term policy costs $43/month at age 35. The same $500K whole life policy costs $490/month. For most Indianapolis families, term life provides the same protection at a fraction of the cost.',
    icon: 'ri-calendar-check-line',
  },
  {
    tip: 'Buy Now, Not Later',
    savings: 'Save 30–60%',
    desc: 'A 35-year-old pays $43/month for $500K coverage. A 45-year-old pays $103/month for the same coverage. Every year you wait costs more — locking in your rate now saves thousands over the life of the policy.',
    icon: 'ri-time-line',
  },
  {
    tip: 'Work with an Independent Agent',
    savings: 'Save 15–25%',
    desc: 'Independent agents compare 20+ carriers simultaneously. Going directly to one carrier means you might be paying 15–25% more than necessary. Hoosier Life Insurance compares all available carriers at no cost to you.',
    icon: 'ri-user-star-line',
  },
  {
    tip: 'Improve Your Health Class',
    savings: 'Save 20–40%',
    desc: 'Losing weight, controlling blood pressure, and improving cholesterol can move you from Standard to Preferred — saving 20–40% on your premium. Even a 10-pound weight loss can make a difference.',
    icon: 'ri-heart-pulse-line',
  },
  {
    tip: 'Quit Smoking',
    savings: 'Save 50–70%',
    desc: 'Smokers pay 2–4x more than non-smokers. After 12 months smoke-free, many carriers reclassify you as a non-smoker. Quitting smoking is the single biggest thing you can do to reduce your life insurance rate.',
    icon: 'ri-forbid-line',
  },
  {
    tip: 'Pay Annually Instead of Monthly',
    savings: 'Save 3–5%',
    desc: 'Most carriers charge a small fee for monthly billing. Paying annually instead of monthly saves 3–5% on your premium — typically $20–$100 per year depending on your coverage amount.',
    icon: 'ri-calendar-2-line',
  },
];

/* ─── Internal Linking Cluster ───────────────────────────────────────────── */
const CLUSTER_LINKS = [
  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis', icon: 'ri-calculator-line' },
  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/term-life-insurance-rates-indianapolis', label: 'Term Life Insurance Rates Indianapolis', icon: 'ri-bar-chart-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-home-heart-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/mortgage-life-insurance-indianapolis', label: 'Mortgage Life Insurance Indianapolis', icon: 'ri-building-line' },
  { to: '/life-insurance-smokers-indianapolis', label: 'Life Insurance for Smokers Indianapolis', icon: 'ri-forbid-line' },
];

/* ─── FAQs ───────────────────────────────────────────────────────────────── */
const FAQS = [
  {
    q: 'What is the cheapest life insurance in Indianapolis?',
    a: 'The cheapest life insurance in Indianapolis is term life insurance from Banner Life or Protective Life. Here are the lowest available rates for healthy non-smokers: Age 25, $250K 20-year term: $14/month. Age 30, $250K 20-year term: $17/month. Age 35, $250K 20-year term: $21/month. Age 40, $250K 20-year term: $32/month. Age 45, $250K 20-year term: $50/month. Age 50, $250K 20-year term: $79/month. These are Preferred Plus rates — the lowest available rate class. Most Indianapolis residents qualify for Preferred or Standard rates, which are slightly higher. An independent agent compares all available carriers to find your cheapest rate.',
  },
  {
    q: 'How can I get cheap life insurance in Indianapolis?',
    a: 'To get the cheapest life insurance in Indianapolis: 1. Choose term life over whole life — term life is 80–90% cheaper for the same death benefit. 2. Buy as young as possible — rates increase with age. 3. Work with an independent agent — they compare 20+ carriers simultaneously, saving you 15–25%. 4. Improve your health before applying — losing weight, controlling blood pressure, and quitting smoking can significantly reduce your rate. 5. Apply for accelerated underwriting — some carriers offer lower rates through online applications without a medical exam. 6. Pay annually instead of monthly — saves 3–5%. 7. Compare multiple carriers — don\'t go directly to one company. Call Hoosier Life Insurance at (317) 648-2983 for a free rate comparison.',
  },
  {
    q: 'Is cheap life insurance in Indianapolis worth it?',
    a: 'Yes — cheap life insurance in Indianapolis is absolutely worth it, as long as you\'re buying from a financially strong carrier. Here\'s what to look for: AM Best rating: Stick with A or A+ rated carriers — Banner Life (A+), Protective Life (A+), Pacific Life (A+), Prudential (A+). These are financially strong companies that will be around to pay your claim. Policy features: Make sure the policy has a conversion option (allows you to convert to permanent coverage without a medical exam) and an accelerated death benefit rider. The key: cheap doesn\'t mean low quality. Banner Life and Protective Life consistently offer the lowest term life rates in Indianapolis while maintaining A+ financial strength ratings. An independent agent can help you find the cheapest life insurance in Indianapolis from a financially strong carrier.',
  },
  {
    q: 'What is the cheapest type of life insurance in Indianapolis?',
    a: 'Term life insurance is the cheapest type of life insurance in Indianapolis. Here\'s how the main types compare for a 35-year-old male in Indianapolis: $500K 20-year term life: $43/month. $500K 30-year term life: $66/month. $500K whole life: $490/month. $500K universal life: $180–$250/month. $500K indexed universal life: $200–$280/month. Term life insurance is 80–90% cheaper than whole life for the same death benefit. For most Indianapolis families who need income replacement and mortgage protection, term life insurance provides the best value. Whole life and universal life make more sense for estate planning, business succession, or wealth building.',
  },
  {
    q: 'Can I get cheap life insurance in Indianapolis with health conditions?',
    a: 'Yes — Indianapolis residents with health conditions can still get affordable life insurance. The key is working with an independent agent who knows which carriers are most lenient with specific conditions. Common conditions and affordable options: High blood pressure (controlled): Standard Plus rate class — 15–25% above Preferred rates. Still very affordable. Type 2 diabetes (controlled): Standard to Table 2 rate class — 40–100% above Preferred rates. Still much cheaper than whole life. High cholesterol (controlled): Standard Plus — 15–25% above Preferred rates. Overweight/obesity: Standard Plus to Standard — 15–40% above Preferred rates. For Indianapolis residents with serious health conditions: Simplified issue life insurance (no medical exam, limited health questions) and guaranteed issue life insurance (no health questions, guaranteed approval) are available — though at higher rates. Call (317) 648-2983 for a free consultation.',
  },
  {
    q: 'What is the cheapest life insurance for seniors in Indianapolis?',
    a: 'The cheapest life insurance for Indianapolis seniors depends on your age and coverage needs. For seniors 50–65: Term life insurance is still the most affordable option. A 55-year-old can get $250K 20-year term for $147/month. For seniors 65–75: Final expense insurance (simplified issue whole life) is often the most affordable option. Coverage of $10,000–$25,000 for $50–$150/month. For seniors 75+: Guaranteed issue life insurance is available regardless of health — $5,000–$25,000 coverage for $50–$200/month. The key for Indianapolis seniors: work with an independent agent who specializes in senior life insurance. Different carriers have very different rates for older applicants. Call (317) 648-2983 for a free senior life insurance rate comparison.',
  },
  {
    q: 'How much life insurance do I need in Indianapolis?',
    a: 'The right amount of life insurance for Indianapolis residents depends on your financial obligations. The DIME method: Debt: Add all debts (mortgage, car loans, credit cards, student loans). Income: Multiply annual income by 10–12 years. Mortgage: Add remaining mortgage balance. Education: Add estimated college costs for each child. For a typical Indianapolis family: $75,000 income × 10 = $750,000. Plus $250,000 mortgage = $1,000,000 total. A $1M 20-year term policy for a healthy 35-year-old costs about $75/month — less than most car insurance payments. The key: don\'t underinsure to save money. The difference between $500K and $1M in coverage is often only $20–$30/month — but the difference in protection for your family is enormous.',
  },
  {
    q: 'What is the difference between cheap and affordable life insurance in Indianapolis?',
    a: '"Cheap" and "affordable" life insurance in Indianapolis are often used interchangeably, but there\'s an important distinction: Cheap life insurance: The lowest possible premium — often achieved by choosing the minimum coverage amount, shortest term, or lowest-rated carrier. Affordable life insurance: The right coverage at the best available rate — achieved by comparing all carriers and choosing the most cost-effective option for your specific needs. The goal: find the most affordable life insurance that provides adequate coverage from a financially strong carrier. An independent agent can help you find the right balance between cost and coverage for your Indianapolis family. Call (317) 648-2983 for a free consultation.',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  {
    name: 'Chris & Amanda T.',
    location: 'Fishers, Indianapolis',
    quote: 'We thought $1M in coverage would be expensive. Benjamin found us $1M 20-year term for $75/month each. That\'s less than our Netflix and Spotify combined. Our kids are fully protected.',
    stars: 5,
    coverage: '$1M 20-year term',
    rate: '$75/month at age 35',
  },
  {
    name: 'Robert K.',
    location: 'Carmel, Indianapolis',
    quote: 'I was paying $142/month with my employer\'s group plan for only $200K coverage. Benjamin found me $500K for $43/month — better coverage at less than a third of the price.',
    stars: 5,
    coverage: '$500K 20-year term',
    rate: 'Saved $99/month',
  },
  {
    name: 'Maria & David L.',
    location: 'Noblesville, Indianapolis',
    quote: 'We\'re on a tight budget with two kids. Benjamin found us $500K each for $33/month each. We had no idea life insurance could be this affordable. Approved in 48 hours.',
    stars: 5,
    coverage: '$500K each, 20-year term',
    rate: '$33/month each at age 30',
  },
  {
    name: 'Susan H.',
    location: 'Broad Ripple, Indianapolis',
    quote: 'Single mom, worried about cost. Benjamin found me $500K for $28/month. I\'m 28 years old and my kids are protected for 20 years. I can\'t believe how affordable it was.',
    stars: 5,
    coverage: '$500K 20-year term',
    rate: '$28/month at age 28',
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */
export default function CheapLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const [bAge, setBAge] = useState<BudgetAge>('35');
  const [bCoverage, setBCoverage] = useState<BudgetCoverage>('500k');
  const [bHealth, setBHealth] = useState<BudgetHealth>('good');
  const [results, setResults] = useState<BudgetResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Cheap Life Insurance Indianapolis IN | Budget Rate Finder | Hoosier Life Insurance';
    const description = 'Find cheap life insurance in Indianapolis, IN. Compare the lowest rates from top carriers. Use our Budget Rate Finder. Term life from $18/month. Call (317) 648-2983.';
    const keywords = 'cheap life insurance Indianapolis, cheap life insurance Indianapolis Indiana, affordable life insurance Indianapolis, low cost life insurance Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/bc13ccaaa9cc090661c509b057f35c7a.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Cheap Life Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: BudgetAge[] = ['25', '30', '35', '40', '45', '50', '55'];
  const coverageOptions: { value: BudgetCoverage; label: string }[] = [
    { value: '100k', label: '$100K' },
    { value: '250k', label: '$250K' },
    { value: '500k', label: '$500K' },
    { value: '1m', label: '$1M' },
  ];
  const healthOptions: { value: BudgetHealth; label: string; desc: string; color: string }[] = [
    { value: 'excellent', label: 'Excellent', desc: 'No conditions, healthy weight', color: 'text-[#4A7C59]' },
    { value: 'good', label: 'Good', desc: 'Minor conditions, controlled', color: 'text-[#D97642]' },
    { value: 'fair', label: 'Fair', desc: 'Some health conditions', color: 'text-red-400' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Cheap Life Insurance</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20happy%20young%20family%20couple%20with%20children%20celebrating%20affordable%20life%20insurance%20approval%20at%20home%20modern%20living%20room%20warm%20natural%20light%20Marion%20County%20Indiana%20relaxed%20joyful%20financial%20security%20photorealistic%20warm%20neutral%20tones%20cream%20beige%20background%20wide%20angle&width=1440&height=700&seq=cheap-li-indy-hero-2025a&orientation=landscape"
            alt="Cheap Life Insurance Indianapolis Indiana — Budget Rate Finder"
            title="Cheap Life Insurance in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-price-tag-3-line text-sm"></i>
              Indianapolis &amp; Marion County · Lowest Rates · 5 Top Carriers · Budget Rate Finder
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Cheap Life Insurance in{' '}
              <span className="text-[#D97642]">Indianapolis, Indiana</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Find the cheapest life insurance rates in Indianapolis. Use our <strong className="text-white">Budget Rate Finder</strong> to see the lowest available rates from Banner Life, Protective, Pacific Life, and more — starting at just $10/month.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '$10/mo', label: 'Cheapest life insurance rate in Indianapolis' },
                { value: '5', label: 'Top carriers compared for lowest rates' },
                { value: '6 Tips', label: 'Money-saving strategies to lower your rate' },
                { value: 'Free', label: 'Rate comparison — no obligation' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#budget-finder" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-price-tag-3-line text-lg"></i>
                Find My Cheapest Rate
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$10/mo', label: 'Cheapest life insurance in Indianapolis — healthy 25-year-old, $100K, 20-year term' },
              { value: '15–25%', label: 'Typical savings when comparing carriers vs. going direct to one company' },
              { value: '80–90%', label: 'Savings when choosing term life over whole life for the same death benefit' },
              { value: '48 hrs', label: 'Fastest approval — accelerated underwriting, no medical exam required' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Cheap Life Insurance in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Term life insurance is the cheapest type — a healthy 30-year-old can get $500K coverage for just $28/month',
                  'Banner Life and Protective Life consistently offer the lowest term life rates in Indianapolis',
                  'Comparing carriers can save 15–25% — don\'t go directly to one company',
                  'Buying now vs. waiting 5 years can save $30–$60/month for the same coverage',
                  'Women pay 10–15% less than men — one of the cheapest demographics for life insurance',
                  'Accelerated underwriting means approval in 24–48 hours with no medical exam for healthy applicants',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT */}
          <div>

            {/* BUDGET RATE FINDER */}
            <div id="budget-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Budget Rate Finder</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Find the Cheapest Life Insurance Rates — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, and health to see the lowest available life insurance rates from the top 5 carriers in Indianapolis.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  {/* Age */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setBAge(a); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${bAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setBCoverage(opt.value); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${bCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Health */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Overall Health</label>
                    <div className="grid grid-cols-3 gap-3">
                      {healthOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setBHealth(opt.value); setResults(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${bHealth === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${bHealth === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setResults(getBudgetResults(bAge, bCoverage, bHealth))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-price-tag-3-line text-lg"></i>
                    Find My Cheapest Rate
                  </button>
                </div>

                {/* Results */}
                {results !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Cheapest Rates — ${COVERAGE_AMOUNTS[bCoverage].toLocaleString()} · Age {bAge} · {bHealth.charAt(0).toUpperCase() + bHealth.slice(1)} Health
                      </h3>
                      {results.length > 0 && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                          From ${Math.min(...results.map(q => q.monthlyRate))}/mo
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-4 mb-6">
                      {results.map((r, i) => (
                        <div key={r.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{r.carrier}</span>
                                  {r.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{r.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {r.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{r.notes}</p>
                                <p className="text-xs text-gray-400 mt-0.5">Approval: {r.approvalTime}</p>
                                {r.tip && <p className="text-xs text-[#4A7C59] font-semibold mt-1">💡 {r.tip}</p>}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${r.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${r.annualRate.toLocaleString()}/yr</div>
                              {r.annualSavings > 0 && (
                                <div className="text-xs text-[#4A7C59] font-semibold">Save ${r.annualSavings}/yr vs. avg</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-[#1A3A52]/5 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates are estimates for illustration purposes. Actual rates depend on full underwriting and carrier selection. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized rate.
                      </p>
                    </div>

                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Lock In My Cheapest Rate — Free 15-Min Call
                    </button>
                  </div>
                )}

                {results === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-price-tag-3-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage, and health above, then click <strong className="text-[#1A3A52]">Find My Cheapest Rate</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Money-Saving Tips */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Money-Saving Strategies</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                6 Ways to Get Cheaper Life Insurance in Indianapolis
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {SAVING_TIPS.map((tip, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                        <i className={`${tip.icon} text-base text-[#D97642]`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-[#1A3A52]">{tip.tip}</span>
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">{tip.savings}</span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{tip.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Families Who Found Cheap Life Insurance
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.rate}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Explore All Indianapolis Life Insurance Topics
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Compare rates, explore coverage options, and find the right policy for your Indianapolis family.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Cheap Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-cheap-li-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-price-tag-3-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Find Your Cheapest Rate</h3>
                    <p className="text-xs text-white/70">20+ carriers compared · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Lowest rates guaranteed</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No obligation</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm">
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · All ages welcome</p>
              </div>
            </div>

            {/* Cheapest Rates Table */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                Cheapest Rates in Indianapolis
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { label: 'Age 25, $250K 20-yr', rate: '$14/mo', note: 'Excellent health' },
                  { label: 'Age 30, $500K 20-yr', rate: '$28/mo', note: 'Excellent health' },
                  { label: 'Age 35, $500K 20-yr', rate: '$36/mo', note: 'Excellent health' },
                  { label: 'Age 40, $500K 20-yr', rate: '$55/mo', note: 'Excellent health' },
                  { label: 'Age 45, $500K 20-yr', rate: '$88/mo', note: 'Excellent health' },
                  { label: 'Age 50, $250K 20-yr', rate: '$90/mo', note: 'Excellent health' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="text-xs font-semibold text-[#1A3A52]">{row.label}</div>
                      <div className="text-xs text-gray-400">{row.note}</div>
                    </div>
                    <span className="text-sm font-bold text-[#4A7C59]">{row.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Banner Life, Preferred Plus health class, non-smoker, male. Women pay ~12% less.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Rate Call</h3>
                    <p className="text-xs text-white/70">Cheapest rates guaranteed · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free Rate Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Budget or coverage questions? (Optional)',
                    submitButton: 'Confirm My Free Rate Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Quotes Indianapolis', icon: 'ri-calculator-line' },
                  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
                  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance', icon: 'ri-money-dollar-circle-line' },
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance', icon: 'ri-stethoscope-line' },
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers', icon: 'ri-search-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to find the cheapest life insurance in Indianapolis?
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Budget Rate Finder above, then call for your exact personalized rate. 20+ carriers compared. No obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#budget-finder" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-price-tag-3-line text-lg"></i>
              Find My Cheapest Rate
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={156} productName="Cheap Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Find Cheap Life Insurance — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
